/* eslint-disable @typescript-eslint/no-floating-promises */
import { Button, DialogActions, DialogContent } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { useReimariApi } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { AtoNMapItem } from "../types/mapItems";
import AtonPopupContent from "./AtoNPopupContent";
import { GenericPopup } from "./GenericPopup";

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: AtoNMapItem;
  hoverData?: AtoNMapItem;
};

type HoverProps = {
  hoverData: AtoNMapItem;
  clickData?: AtoNMapItem;
};

export const AtoNPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const { getAtonUnit } = useReimariApi();
  const data = clickData ?? hoverData;
  const { show } = usePopup();
  const isMobile = useAppSelector((s) => s.general.isMobile);
  const { t } = useTranslation();

  const { data: details, isLoading } = useCustomQuery(
    { key: "aton", id: clickData?.atonNumber },
    () => getAtonUnit(clickData!.atonNumber),
    {
      enabled:
        clickData?.atonNumber !== undefined && clickData?.atonNumber !== null,
      refetchOnWindowFocus: false,
    },
  );

  React.useEffect(() => {
    let close: () => void;
    if (details) {
      show(
        ({ close: onClose }) => {
          close = onClose;
          return (
            <>
              <DialogContent sx={{ padding: 0 }}>
                <AtonPopupContent aton={details} />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{t("general:close")}</Button>
              </DialogActions>
            </>
          );
        },
        {
          dialogProps: {
            maxWidth: isMobile ? undefined : "lg",
            fullScreen: isMobile,
            fullWidth: true,
          },
        },
      );
    }

    return function cleanup() {
      close?.();
    };
  }, [details, isMobile, show, t]);

  if (!data) return null;

  const { name, atonType, subType, navigationType } = data;

  return (
    <GenericPopup
      map={map}
      loading={isLoading}
      clicked={!!clickData}
      avatarText={name[0]}
      title={name}
      subheader={`${atonType} - ${subType} - ${navigationType}`}
      contentProps={{ style: { padding: 0 } }}
    />
  );
};
